import { Component, OnInit } from '@angular/core';

import { UiLibraryModule } from '@template-ui/template-ui.module';
import { ButtonClickEvent, EzFormInputType, InputChangeEvent, InputConfig } from '@template-ui/types';

import { CheckboxColumnsComponent } from '../checkbox-columns/checkbox-columns.component';

type DlogIdentifierStatus = {
  identifier: string;
  enabled: boolean;
};

type DlogApi = {
  readonly get: { enabled: boolean; identifiers: string[] };
  set: (partial: Partial<{ enabled: boolean; identifiers: string[] }>) => { enabled: boolean; identifiers: string[] };
  on: (name: string) => { enabled: boolean; identifiers: string[] };
  off: (name: string) => { enabled: boolean; identifiers: string[] };
  readonly ls: DlogIdentifierStatus[];
};

@Component({
  selector: 'app-configure-dlog',
  imports: [UiLibraryModule, CheckboxColumnsComponent],
  templateUrl: './configure-dlog.component.html',
  styleUrl: './configure-dlog.component.scss',
})
export class ConfigureDlogComponent implements OnInit {
  protected dlogReady = false;
  protected dlogFilter = '';
  protected dlogRows: DlogIdentifierStatus[] = [];

  protected readonly toolbarConfig: InputConfig[] = [

    {
      name: 'dlogEnableAll',
      type: EzFormInputType.BUTTON,
      buttonText: 'Check All',
      row: 1,
    },
    {
      name: 'dlogDisableAll',
      type: EzFormInputType.BUTTON,
      buttonText: 'Uncheck All',
      row: 1,
    },
    {
      name: 'dlogFilter',
      label: 'Filter',
      type: EzFormInputType.TEXT,
      placeholder: 'Filter identifiers...',
      width: '1x',
      row: 1,
    },
    // {
    //   name: 'dlogReset',
    //   type: EzFormInputType.BUTTON,
    //   buttonText: 'Reset',
    //   row: 1,
    // },
  ];

  public ngOnInit(): void {
    this.refreshDlogState();
  }

  protected get filteredDlogRows(): DlogIdentifierStatus[] {
    const filter = this.dlogFilter.trim().toLowerCase();
    if (filter.length < 1) {
      return this.dlogRows;
    }

    return this.dlogRows.filter((row) => row.identifier.toLowerCase().includes(filter));
  }

  protected get filteredDlogCheckboxes(): Array<{ label: string; isChecked: boolean }> {
    return this.filteredDlogRows.map((row) => ({
      label: row.identifier,
      isChecked: row.enabled,
    }));
  }

  protected get toolbarValues(): Record<string, string | boolean> {
    return {
      dlogFilter: this.dlogFilter,
    };
  }

  protected onToolbarInputChange(event: InputChangeEvent<unknown>): void {
    if (event.name !== 'dlogFilter') {
      return;
    }

    this.dlogFilter = typeof event.value === 'string' ? event.value : String(event.value ?? '');
  }

  protected onToolbarButtonClick(event: ButtonClickEvent): void {
    const api = this.getDlogApi();
    if (!api) {
      return;
    }

    if (event.name === 'dlogEnableAll') {
      api.set({ enabled: true });
      for (const row of this.dlogRows) {
        api.on(row.identifier);
      }
      this.refreshDlogState();
      return;
    }

    if (event.name === 'dlogDisableAll') {
      api.set({ enabled: false });
      for (const row of this.dlogRows) {
        api.off(row.identifier);
      }
      this.refreshDlogState();
      return;
    }

    if (event.name === 'dlogReset') {
      api.set({ enabled: false, identifiers: [] });
      this.refreshDlogState();
    }
  }

  protected onCheckboxColumnsToggled(event: { label: string; isChecked: boolean }): void {
    const api = this.getDlogApi();
    if (!api) {
      return;
    }

    if (event.isChecked) {
      api.on(event.label);
    } else {
      api.off(event.label);
    }

    this.refreshDlogState();
  }

  private refreshDlogState(): void {
    const api = this.getDlogApi();
    if (!api) {
      this.dlogReady = false;
      this.dlogRows = [];
      return;
    }

    this.dlogReady = true;
    this.dlogRows = [...(api.ls ?? [])].sort((a, b) => a.identifier.localeCompare(b.identifier));
  }

  private getDlogApi(): DlogApi | undefined {
    const scope = globalThis as { dlog?: DlogApi };
    return scope.dlog;
  }
}
